import React, { useEffect, useMemo, useRef, useState, type FunctionComponent } from "react";
import { ArrowLeftOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import type EditorJS from "@editorjs/editorjs";
import { type OutputData } from "@editorjs/editorjs";
import { Button, Card, Divider, Form, Input, message, Select, Space } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { mediaLibraryActions, productResourcesActions } from "@fitness-app/app-store";
import { CoverTarget } from "@fitness-app/data-models/entities/ImageGeneration";
import { MediaLibraryResourceType } from "@fitness-app/data-models/entities/MediaLibrary";
import {
  ProductResourceStatus,
  type ProductResourceStatusEnum,
} from "@fitness-app/data-models/entities/ProductResource";
import { DEFAULT_RESOURCE_CATEGORY } from "@fitness-app/utils/src/constants/defaultResourceCategory";

import Editor from "~/components/Editor/Editor";
import { extractPlainText } from "~/components/Editor/extractPlainText";
import { createTools } from "~/components/Editor/tools";
import ImageUploadField from "~/components/ImageUploadField/ImageUploadField";
import UploadField from "~/components/UploadField/UploadField";
import { useEntityChange } from "~/hooks/useEntityChange";
import MediaLibrary from "~/modules/MediaLibrary/MediaLibrary";
import ProductCoverAiGenerator from "~/modules/Products/ProductForm/ProductCoverAiGenerator";
import { productResourceStatusOptions } from "~/modules/Products/Product/ProductResources/shared";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface OwnProps {
  productId: string;
}

type Props = OwnProps;

interface ResourceEditorModel {
  title: string;
  content: string | OutputData;
  attachments: { url: string; name: string; uid: string }[];
  images: string[];
  status: ProductResourceStatusEnum;
  category: string;
}

const ENTER_KEY = 13;

const ResourceEditor: FunctionComponent<Props> = ({ productId }) => {
  const { t } = useTranslation(["products", "common"]);
  const [formController] = Form.useForm<ResourceEditorModel>();
  const editorRef = useRef<EditorJS>(null);
  const userDetails = useAppSelector((store) => store.user.profile);
  const { resourceId } = useParams<{ resourceId: string }>();
  const resources = useAppSelector((store) => store.productResources.data);
  const categories = useAppSelector((store) => store.productResources.categories);
  const editedResource = resources.find((item) => item.id === resourceId);
  const [categoryInput, setCategoryInputValue] = useState("");
  const [thumbnailKey, remountThumbnailField] = useState(0);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const images = Form.useWatch("images", formController);
  const resourceTitle = Form.useWatch("title", formController);
  const selectedCategory = Form.useWatch("category", formController);
  // Kept in a ref, not state - the editor fires on every edit and the form has no reason to
  // re-render for it. Only the generator reads it, and only when the button is clicked.
  const resourceContentRef = useRef("");

  const [loading, onSuccess, onFailure] = useEntityChange(() => {
    navigate(-1);
  });

  const content = editedResource?.content;
  const resourceCategories = categories?.length
    ? [{ name: DEFAULT_RESOURCE_CATEGORY, default: true, id: "default" }, ...categories]
    : [{ name: DEFAULT_RESOURCE_CATEGORY, default: true, id: "default" }];

  const editorModel = useMemo(() => (content ? JSON.parse(content) : ""), [content]) as OutputData;

  const categoriesOptions = useMemo(() => {
    if (!resourceCategories?.length) {
      return [
        {
          value: DEFAULT_RESOURCE_CATEGORY,
          label: t("productResources.form.general"),
        },
      ];
    }
    return resourceCategories.map((category) =>
      "default" in category && category.default
        ? { value: category.name, label: t("productResources.form.general") }
        : { value: category.id, label: category.name },
    );
  }, [resourceCategories]);

  useEffect(() => {
    if (editedResource) {
      formController?.setFieldsValue({
        ...editedResource,
        status: editedResource.status || ProductResourceStatus.DRAFT,
        category: editedResource.category || DEFAULT_RESOURCE_CATEGORY,

        content: JSON.parse(editedResource.content ?? "") as OutputData,
      });
    }
  }, [resourceId, editedResource]);

  // Both the `video` block tool and the library button above only read the list - the subscription
  // belongs to the screen, which outlives either of them.
  useEffect(() => {
    dispatch(mediaLibraryActions.subscribeToMediaLibrary(MediaLibraryResourceType.ProductResource));
    return () => {
      dispatch(mediaLibraryActions.unsubscribeFromMediaLibrary());
    };
  }, []);

  const storageRef = useMemo(() => `${userDetails?.id}/products/${productId}/resources`, [userDetails, productId]);

  const tools = createTools({ storageRef });

  if (!productId || !userDetails?.id || !tools) {
    return null;
  }

  // ImageUploadField only reads `fileList` on mount, so applying a generated thumbnail has to
  // remount it - otherwise the form value is right but the preview stays on the old image.
  const setGeneratedThumbnail = (url: string) => {
    formController.setFieldsValue({ images: [url] });
    remountThumbnailField((key) => key + 1);
  };

  const handleSubmit = async (data: ResourceEditorModel) => {
    const savedData = await editorRef.current?.save();
    const contentInJSON = JSON.stringify(savedData);

    if (editedResource) {
      const model = {
        ...data,
        id: editedResource.id,
        htmlContent: contentInJSON,
        content: contentInJSON,
      };

      void dispatch(
        productResourcesActions.updateProductResource({
          payload: { productId, model },
          onFailure,
          onSuccess,
        }),
      );
    } else {
      const model = {
        ...data,
        type: "IN_APP",
        category: data.category || "general",
        htmlContent: contentInJSON,
        content: contentInJSON,
      } as const;

      void dispatch(
        productResourcesActions.addProductResource({
          payload: { productId, model },
          onFailure,
          onSuccess,
        }),
      );
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const pressSendButton = event.keyCode === ENTER_KEY && !event.shiftKey;

    if (pressSendButton) {
      addCategory();
    }
  };

  const addCategory = () => {
    if (!categoryInput || categoryInput.length < 3) {
      void message.warning(t("common:validationErrors.minLength", { number: 3 }));
      return;
    }

    if (categoriesOptions.find((category) => category.label?.toLowerCase() === categoryInput.toLowerCase())) {
      void message.warning(t("productResources.form.uniqCategory", { number: 3 }));
      return;
    }

    void dispatch(
      productResourcesActions.addResourceCategory({
        model: {
          name: categoryInput,
        },
        productId,
      }),
    );

    const selectedCategory = categoryInput;

    setTimeout(() => {
      formController.setFieldsValue({
        category: selectedCategory,
      });
    }, 200);

    setCategoryInputValue("");
  };

  return (
    <Card
      style={{ minHeight: 500 }}
      // Absolute path, not navigate(-1): the editor is also opened from a pasted link, and there
      // the previous entry is whatever the person had open before the admin.
      title={
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          style={{ paddingInline: 0 }}
          onClick={() => navigate(`/products/${productId}/resources`)}
        >
          {t("productResources.backToList")}
        </Button>
      }
      extra={
        <Space>
          <MediaLibrary type={MediaLibraryResourceType.ProductResource} subscribe={false} />
          <Button
            loading={Boolean(loading)}
            icon={<SaveOutlined />}
            type="primary"
            onClick={() => formController?.submit()}
          >
            {editedResource ? t("button.update") : t("button.save")}
          </Button>
        </Space>
      }
    >
      <Form<ResourceEditorModel>
        layout="vertical"
        onFinish={handleSubmit}
        form={formController}
        initialValues={{
          title: "",
          content: editedResource?.content ?? "",
          images: editedResource?.images ?? [],
          attachments: editedResource?.attachments ?? [],
          status: editedResource?.status ?? ProductResourceStatus.DRAFT,
          category: editedResource?.category || DEFAULT_RESOURCE_CATEGORY,
        }}
      >
        <Form.Item
          name="title"
          label={t("productResources.form.title")}
          wrapperCol={{ span: 12 }}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
            {
              min: 5,
              message: t<string>("common:validationErrors.minLength", {
                number: 5,
              }),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="category"
          label={t("productResources.form.category")}
          wrapperCol={{ xl: 6, lg: 12 }}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Select
            options={categoriesOptions}
            placeholder={t("productResources.form.selectCategory")}
            dropdownRender={(menu) => (
              <div>
                {menu}
                <Divider style={{ margin: "4px 0" }} />
                <div style={{ display: "flex", flexWrap: "nowrap", padding: 8 }}>
                  <Input
                    style={{ flex: "auto" }}
                    value={categoryInput}
                    size="small"
                    onKeyDown={handleInputKeyDown}
                    onChange={(e) => setCategoryInputValue(e.target.value)}
                  />
                  <a
                    style={{
                      flex: "none",
                      padding: "8px",
                      display: "block",
                      cursor: "pointer",
                    }}
                    onClick={addCategory}
                  >
                    <PlusOutlined /> {t("productResources.form.addCategory")}
                  </a>
                </div>
              </div>
            )}
          />
        </Form.Item>

        <Form.Item
          name="status"
          label={t("productResources.form.status")}
          wrapperCol={{ xl: 6, lg: 12 }}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
            {
              min: 5,
              message: t<string>("common:validationErrors.minLength", {
                number: 5,
              }),
            },
          ]}
        >
          <Select options={productResourceStatusOptions(t)} />
        </Form.Item>

        <Form.Item
          tooltip={t<string>("products:form.imageAdditionalInfo")}
          name="images"
          label={t<string>("productResources.form.images")}
          valuePropName="fileList"
          getValueFromEvent={(e: unknown) => e}
          style={{ marginBottom: 8 }}
        >
          <ImageUploadField
            key={thumbnailKey}
            storageRef={`${userDetails.id}/products/${productId}/resources`}
            bucket="users"
            multiple={false}
            withCrop={false}
            fileList={images}
          />
        </Form.Item>

        <Form.Item>
          <ProductCoverAiGenerator
            target={CoverTarget.PRODUCT_RESOURCE}
            productId={productId}
            productContext={() => ({
              name: resourceTitle ?? "",
              description: resourceContentRef.current || null,
              productType: categoriesOptions.find((option) => option.value === selectedCategory)?.label ?? null,
            })}
            setCoverUrl={setGeneratedThumbnail}
          />
        </Form.Item>

        <Form.Item
          tooltip={t<string>("products:form.fileAdditionalInfo")}
          name="attachments"
          label={t<string>("productResources.form.attachments")}
          valuePropName="fileList"
          getValueFromEvent={(e: unknown) => e}
          wrapperCol={{ span: 12 }}
        >
          <UploadField
            multiple
            bucket="users"
            allowVideoFiles={false}
            fileList={editedResource?.attachments}
            storageRef={`${userDetails.id}/products/${productId}/resources`}
          />
        </Form.Item>

        <Form.Item name="content" label={t<string>("productResources.form.content")}>
          {editorModel || resourceId === "add" ? (
            <Editor
              tools={tools}
              data={editorModel}
              onChange={(_api, data) => {
                // The wrapper hands over the saved document; the mutation-event overloads that
                // EditorJS's own config type allows never reach this callback.
                resourceContentRef.current = extractPlainText(data && "blocks" in data ? data : null);
              }}
              instanceRef={(instance) => {
                editorRef.current = instance;
              }}
            />
          ) : null}
        </Form.Item>
      </Form>
    </Card>
  );
};

export default ResourceEditor;
